/**
 * Zustand store for authentication state.
 *
 * State:
 *   - user: UserProfile | null — the currently authenticated admin user
 *   - isAuthenticated: boolean — derived from user !== null
 *   - isLoading: boolean — true while any auth request is in flight
 *
 * Actions:
 *   - login(email, password)  — POST /api/v1/auth/login; sets user in state + localStorage
 *   - logout()                — POST /api/v1/auth/logout; clears state + localStorage
 *   - refreshToken()          — POST /api/v1/auth/refresh; uses httpOnly cookie automatically
 *   - loadUser()              — GET  /api/v1/auth/me; hydrates state from existing session cookie
 *
 * Persistence:
 *   - Persists: user profile (non-sensitive) to localStorage key "helix_user"
 *   - Does NOT persist tokens — those live in httpOnly cookies managed by the server
 *
 * Integration:
 *   - On app init: call loadUser() in the root component to restore session
 *   - On 401 HTTP response (via axios interceptor): auto-calls refreshToken();
 *     if refresh fails → logout() and redirect to /login
 *   - queryClient error handler also triggers logout on unrecoverable 401
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { UserProfile } from '../types/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthState {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

export interface AuthActions {
  login: (email: string, password: string) => Promise<void>;
  requestOtp: (email: string) => Promise<void>;
  verifyOtp: (email: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  loadUser: () => Promise<void>;
  clearError: () => void;
}

export type AuthStore = AuthState & AuthActions;

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const API_BASE = '/api/v1';

interface BackendUser {
  id: string;
  email: string;
  full_name: string;
  role: string;
  org_id: string;
  is_active: boolean;
  created_at: string;
  last_login_at?: string;
}

interface AuthResponse {
  token_type: string;
  user: BackendUser;
}

function normalizeUser(u: BackendUser): UserProfile {
  return {
    id: u.id,
    email: u.email,
    name: u.full_name,
    org_id: u.org_id,
    role: u.role as UserProfile['role'],
    created_at: u.created_at,
    last_login_at: u.last_login_at,
  };
}

async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include', // send httpOnly cookies
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(body.message ?? `HTTP ${res.status}`);
  }

  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // ── State ──────────────────────────────────────────────────────────────
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      // ── Actions ────────────────────────────────────────────────────────────

      /**
       * Authenticate the admin user.
       * The server sets an httpOnly session cookie on success.
       * We store only the non-sensitive UserProfile in localStorage.
       */
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const normalizedEmail = email.trim().toLowerCase();
          const normalizedPassword = password.trim();
          const res = await apiFetch<AuthResponse>('/auth/login', {
            method: 'POST',
            body: JSON.stringify({ email: normalizedEmail, password: normalizedPassword }),
          });
          set({ user: normalizeUser(res.user), isAuthenticated: true, isLoading: false });
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : 'Login failed',
            isLoading: false,
          });
          throw err;
        }
      },

      requestOtp: async (email: string) => {
        set({ isLoading: true, error: null });
        try {
          await apiFetch('/auth/otp/request', {
            method: 'POST',
            body: JSON.stringify({ email: email.trim().toLowerCase() }),
          });
          set({ isLoading: false });
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : 'Unable to send the sign-in code',
            isLoading: false,
          });
          throw err;
        }
      },

      verifyOtp: async (email: string, code: string) => {
        set({ isLoading: true, error: null });
        try {
          const res = await apiFetch<AuthResponse>('/auth/otp/verify', {
            method: 'POST',
            body: JSON.stringify({ email: email.trim().toLowerCase(), code }),
          });
          set({ user: normalizeUser(res.user), isAuthenticated: true, isLoading: false });
        } catch (err) {
          set({
            error: err instanceof Error ? err.message : 'Invalid or expired sign-in code',
            isLoading: false,
          });
          throw err;
        }
      },

      /**
       * Sign out the current user.
       * Calls the server to invalidate the session cookie, then clears local state.
       */
      logout: async () => {
        set({ isLoading: true });
        try {
          await apiFetch('/auth/logout', { method: 'POST' });
        } catch {
          // Best-effort — clear local state regardless of server response
        } finally {
          set({ user: null, isAuthenticated: false, isLoading: false, error: null });
          // Clear persisted user from localStorage
          localStorage.removeItem('helix_user');
        }
      },

      /**
       * Silently refresh the access token using the httpOnly refresh cookie.
       * Returns true if refresh succeeded, false if it failed (session expired).
       * Called automatically by axios interceptors on 401 responses.
       */
      refreshToken: async (): Promise<boolean> => {
        try {
          const res = await apiFetch<AuthResponse>('/auth/refresh', { method: 'POST' });
          set({ user: normalizeUser(res.user), isAuthenticated: true });
          return true;
        } catch {
          await get().logout();
          return false;
        }
      },

      /**
       * Hydrate the store from an existing server session (on app init).
       * Calls GET /auth/me with the existing cookie.
       */
      loadUser: async () => {
        set({ isLoading: true });
        try {
          const u = await apiFetch<BackendUser>('/auth/me');
          set({ user: normalizeUser(u), isAuthenticated: true, isLoading: false });
        } catch {
          set({ user: null, isAuthenticated: false, isLoading: false });
        }
      },

      /** Clear the error field (e.g. after showing it in the UI). */
      clearError: () => set({ error: null }),
    }),
    {
      name: 'helix_user',
      // Only persist non-sensitive user profile data
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);
